import { ZuruflexApiError } from '@zuruflex/api-client';
import type { Review } from '@zuruflex/types';
import { formatDateIn, toLocale } from '@zuruflex/i18n';
import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  FormField,
  PageHeader,
  RatingStars,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@zuruflex/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';

type Decision = { review: Review; action: 'HIDDEN' | 'REMOVED' | 'PUBLISHED' };

/**
 * The moderation queue.
 *
 * Cards rather than a table, deliberately. A review is a paragraph of prose,
 * and a row truncates it — a Hide button beside three words of a complaint is
 * the same mistake `PartnerDetailPage` records having already made once: a
 * decision taken without looking at the thing it is about.
 */
export function ReviewsPage() {
  const { t } = useTranslation(['admin', 'common']);
  const [deciding, setDeciding] = useState<Decision | null>(null);

  return (
    <div className="space-y-6">
      <PageHeader title={t('reviews.title')} description={t('reviews.subtitle')} />

      <Tabs defaultValue="reported">
        <TabsList>
          <TabsTrigger value="reported">{t('reviews.tab.reported')}</TabsTrigger>
          <TabsTrigger value="guests">{t('reviews.tab.guests')}</TabsTrigger>
          <TabsTrigger value="low">{t('reviews.tab.low')}</TabsTrigger>
          <TabsTrigger value="hidden">{t('reviews.tab.hidden')}</TabsTrigger>
        </TabsList>

        <TabsContent value="reported" className="pt-4">
          <ReportedList onDecide={setDeciding} />
        </TabsContent>
        <TabsContent value="guests" className="pt-4">
          {/* The highest-risk prose in the product gets eyes on it by default,
              not only when somebody complains. */}
          <ReviewList query={{ side: 'HOST_ON_GUEST' }} onDecide={setDeciding} />
        </TabsContent>
        <TabsContent value="low" className="pt-4">
          <ReviewList query={{ side: 'GUEST_ON_LISTING' }} lowOnly onDecide={setDeciding} />
        </TabsContent>
        <TabsContent value="hidden" className="pt-4">
          <ReviewList query={{ status: 'HIDDEN' }} onDecide={setDeciding} />
        </TabsContent>
      </Tabs>

      {deciding ? (
        <DecisionDialog decision={deciding} onClose={() => setDeciding(null)} />
      ) : null}
    </div>
  );
}

function ReportedList({ onDecide }: { onDecide: (decision: Decision) => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();
  const reports = useQuery({
    queryKey: ['review-reports'],
    queryFn: () => api.listReviewReports({ perPage: 50 }),
    placeholderData: keepPreviousData,
  });

  const resolve = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'UPHELD' | 'DISMISSED' }) =>
      api.resolveReviewReport(id, {
        status,
        resolution:
          status === 'DISMISSED' ? t('reviews.dismissedDefault') : t('reviews.upheldDefault'),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['review-reports'] });
      void queryClient.invalidateQueries({ queryKey: ['reviews'] });
    },
  });

  if (reports.error) {
    return <ErrorState error={reports.error} onRetry={() => void reports.refetch()} />;
  }
  if (reports.isPending) return <Skeleton className="h-40 w-full" />;

  const items = (reports.data?.items ?? []) as Array<{
    id: string;
    reason: string;
    detail: string | null;
    createdAt: string;
    review: Review;
  }>;

  if (items.length === 0) {
    return <EmptyState title={t('reviews.noReports')} description={t('reviews.noReportsBody')} />;
  }

  return (
    <ul className="flex flex-col gap-4">
      {items.map((report) => (
        <li key={report.id}>
          <ReviewCardFull
            review={report.review}
            onDecide={onDecide}
            footer={
              <div className="space-y-3">
                <Alert variant="warning">
                  <span className="font-semibold">
                    {t(`common:rating.reportReason.${report.reason}`)}
                  </span>
                  {report.detail ? <p className="mt-1">{report.detail}</p> : null}
                </Alert>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={resolve.isPending}
                    onClick={() => resolve.mutate({ id: report.id, status: 'UPHELD' })}
                  >
                    {t('reviews.uphold')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={resolve.isPending}
                    onClick={() => resolve.mutate({ id: report.id, status: 'DISMISSED' })}
                  >
                    {t('reviews.dismiss')}
                  </Button>
                </div>
              </div>
            }
          />
        </li>
      ))}
    </ul>
  );
}

function ReviewList({
  query,
  lowOnly,
  onDecide,
}: {
  query: Record<string, string>;
  lowOnly?: boolean;
  onDecide: (decision: Decision) => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const reviews = useQuery({
    queryKey: ['reviews', query],
    queryFn: () => api.listReviews({ ...query, perPage: 50 }),
    placeholderData: keepPreviousData,
  });

  if (reviews.error) {
    return <ErrorState error={reviews.error} onRetry={() => void reviews.refetch()} />;
  }
  if (reviews.isPending) return <Skeleton className="h-40 w-full" />;

  /*
   * Filtered here rather than in the API: a "1 or 2 stars" screen is one
   * team's way of triaging, not a shape the endpoint should grow a parameter
   * for until more than one caller wants it.
   */
  const items = (reviews.data?.items ?? []).filter(
    (review) => !lowOnly || review.overall <= 2,
  );

  if (items.length === 0) {
    return <EmptyState title={t('reviews.none')} description={t('reviews.noneBody')} />;
  }

  return (
    <ul className="flex flex-col gap-4">
      {items.map((review) => (
        <li key={review.id}>
          <ReviewCardFull review={review} onDecide={onDecide} />
        </li>
      ))}
    </ul>
  );
}

/** The review in full, with everything needed to judge it on the same card. */
function ReviewCardFull({
  review,
  onDecide,
  footer,
}: {
  review: Review;
  onDecide: (decision: Decision) => void;
  footer?: React.ReactNode;
}) {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);

  return (
    <div className="space-y-4 rounded-card border border-border-default bg-surface-raised p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <RatingStars value={review.overall} size="sm" />
            <span className="font-semibold">{review.authorName}</span>
            <span className="text-text-muted">→</span>
            <span>{review.subjectName}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-text-muted">
            <Link
              to={`/bookings/${review.bookingReference}`}
              className="font-mono underline underline-offset-2"
            >
              {review.bookingReference}
            </Link>
            <span>·</span>
            <span>{formatDateIn(review.submittedAt, locale)}</span>
            <span>·</span>
            <span>{t(`reviews.source.${review.source}`)}</span>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {review.status !== 'PUBLISHED' ? (
            <Badge variant="warning">{t(`common:rating.state.${review.status}`)}</Badge>
          ) : null}
          {!review.revealed ? <Badge variant="default">{t('reviews.blind')}</Badge> : null}
        </div>
      </div>

      {review.body ? (
        <p className="whitespace-pre-line text-sm leading-relaxed">{review.body}</p>
      ) : (
        <p className="text-sm italic text-text-muted">{t('reviews.noWords')}</p>
      )}

      {review.hiddenReason ? (
        <Alert variant="warning">{t('reviews.hiddenFor', { reason: review.hiddenReason })}</Alert>
      ) : null}

      {footer}

      <div className="flex flex-wrap gap-2 border-t border-border-subtle pt-3">
        {review.status === 'PUBLISHED' ? (
          <Button size="sm" variant="ghost" onClick={() => onDecide({ review, action: 'HIDDEN' })}>
            {t('reviews.hide')}
          </Button>
        ) : review.status === 'HIDDEN' ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDecide({ review, action: 'PUBLISHED' })}
          >
            {t('reviews.restore')}
          </Button>
        ) : null}
        {review.status !== 'REMOVED' ? (
          <Button
            size="sm"
            variant="destructive"
            onClick={() => onDecide({ review, action: 'REMOVED' })}
          >
            {t('reviews.remove')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Hide, restore or remove.
 *
 * Removal is confirmed and hiding is not, because only one of them is
 * irreversible — and a moderation tool that destroys a review on a single
 * misclick is a bug, not a fast workflow.
 */
function DecisionDialog({ decision, onClose }: { decision: Decision; onClose: () => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const moderate = useMutation({
    mutationFn: () =>
      api.moderateReview(decision.review.id, {
        status: decision.action,
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reviews'] });
      void queryClient.invalidateQueries({ queryKey: ['review-reports'] });
      onClose();
    },
  });

  const takingDown = decision.action !== 'PUBLISHED';
  const key = decision.action === 'REMOVED' ? 'remove' : decision.action === 'HIDDEN' ? 'hide' : 'restore';

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(`reviews.confirm.${key}Title`)}</DialogTitle>
          <DialogDescription>
            {t(`reviews.confirm.${key}Body`, { author: decision.review.authorName })}
          </DialogDescription>
        </DialogHeader>

        {takingDown ? (
          <FormField label={t('reviews.reason')} hint={t('reviews.reasonHint')}>
            {(field) => (
              <Textarea
                id={field.id}
                rows={3}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            )}
          </FormField>
        ) : null}

        {moderate.isError ? (
          <Alert variant="danger">
            {moderate.error instanceof ZuruflexApiError
              ? moderate.error.message
              : t('common:error.unexpected')}
          </Alert>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common:action.cancel')}
          </Button>
          <Button
            variant={decision.action === 'REMOVED' ? 'destructive' : 'default'}
            isLoading={moderate.isPending}
            disabled={takingDown && !reason.trim()}
            onClick={() => moderate.mutate()}
          >
            {t(`reviews.${key}`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
